import React, { useState, useRef, useCallback } from 'react';
import { Flame } from 'lucide-react';
import { cn } from '../../utils/cn';
import { useOverallStreak } from '../../hooks/useOverallStreak';
import { useLayoutVariant } from '../../hooks/useLayoutVariant';
import { StreakPopover, StreakCalendar } from './StreakCalendar';

interface OverallStreakCounterProps {
  className?: string;
}

export const OverallStreakCounter: React.FC<OverallStreakCounterProps> = ({ className }) => {
  const { currentStreak, maxStreak } = useOverallStreak();
  const layoutVariant = useLayoutVariant();
  const isMobile = layoutVariant.type === 'mobile';
  const streakAccent = 'text-amber-700 dark:text-amber-300';

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const togglePopover = useCallback(() => {
    setIsPopoverOpen((prev) => !prev);
  }, []);

  const closePopover = useCallback(() => {
    setIsPopoverOpen(false);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        togglePopover();
      }
    },
    [togglePopover]
  );

  return (
    <>
      <div
        ref={anchorRef}
        className={cn(
          'inline-flex items-center gap-1 text-foreground',
          'font-medium tracking-tight cursor-pointer select-none',
          'hover:opacity-80 transition-opacity',
          'text-xs',
          isMobile && 'gap-0.5',
          className
        )}
        role="button"
        tabIndex={0}
        aria-expanded={isPopoverOpen}
        aria-haspopup="dialog"
        aria-label={`Overall streak: ${currentStreak} consecutive days. Click to view calendar.`}
        title="Streak: Play any category for consecutive days"
        onClick={togglePopover}
        onKeyDown={handleKeyDown}
      >
        <Flame
          className={cn('w-3 h-3', streakAccent)}
          strokeWidth={2.4}
          aria-hidden="true"
        />
        {!isMobile && (
          <span className={cn('font-normal', streakAccent)}>
            Streak:
          </span>
        )}
        <span className="font-bold tabular-nums">
          {currentStreak}
        </span>
      </div>

      {isPopoverOpen && (
        <StreakPopover anchorRef={anchorRef} onClose={closePopover}>
          <StreakCalendar
            currentStreak={currentStreak}
            maxStreak={maxStreak}
          />
        </StreakPopover>
      )}
    </>
  );
};
